import React from "react";

export default function Privacy() {
  const effectiveDate = new Date().toLocaleDateString();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-2xl p-8">
        <h1 className="text-3xl font-bold mb-2 text-center text-gray-900">
          Privacy Policy
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Effective date: {effectiveDate}
        </p>

        {/* Intro */}
        <section className="space-y-3 mb-8 text-gray-800">
          <p>
            SatStacker (“SatStacker,” “we,” “us,” or “our”) respects your privacy.
            This Privacy Policy explains what information we collect, how we use
            it, how we store it, and the choices you have.
          </p>
          <p>
            SatStacker is a planning, automation, and tracking app for recurring
            Bitcoin purchases (“DCA”). SatStacker does <strong>not</strong> buy,
            sell, exchange, transmit, or custody cryptocurrency or fiat funds.
            Bitcoin transactions, custody, exchange, transfer, settlement, KYC/AML,
            eligibility, limits, and compliance controls are handled by the
            applicable third-party provider through your own provider account.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Information We Collect
          </h2>

          <p>
            We collect the minimum information required to operate SatStacker. This
            may include:
          </p>

          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>Account information:</strong> email address, and a unique user
              identifier associated with your account.
            </li>
            <li>
              <strong>Authentication information:</strong> login session data and
              tokens required to keep you signed in (stored securely).
            </li>
            <li>
              <strong>Preferences and app data you provide:</strong> DCA plans,
              settings, and other configuration data you enter into the app.
            </li>
            <li>
              <strong>Provider connection data:</strong> if you connect or use a
              supported third-party provider, we may receive account connection
              status, authorized OAuth/API data, transaction-related information,
              plan execution information, balances, or other provider account
              information needed to operate SatStacker features, depending on the
              permissions you authorize or the integration made available.
            </li>
            <li>
              <strong>Third-party platform data:</strong> if you use SatStacker-powered
              functionality through an approved third-party platform, we may receive
              limited information from that platform as needed to provide, support,
              secure, or improve the SatStacker-powered functionality.
            </li>
            <li>
              <strong>Support communications:</strong> information you provide when
              you contact us for help (e.g., email content).
            </li>
          </ul>

          <p className="text-gray-700">
            <strong>We do not</strong> request access to your Google Drive, contacts,
            calendars, photos, or other files.
          </p>
        </section>

        {/* Third-Party Authentication (Google & Apple) */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Third-Party Authentication Providers (Google & Apple)
          </h2>

          <p>
            SatStacker allows you to sign in using third-party authentication
            providers, including Google Sign-In and Sign in with Apple.
          </p>

          <p>
            When you sign in using one of these providers, we may receive:
          </p>

          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>Email address</strong> (or a private relay address if you use
              Apple’s “Hide My Email” feature)
            </li>
            <li>
              <strong>A unique user identifier</strong> provided by the authentication
              service
            </li>
            <li>
              <strong>Basic profile information</strong>, if provided by the service
              (for example, display name)
            </li>
          </ul>

          <p>We use this information solely to:</p>

          <ul className="list-disc list-inside space-y-2">
            <li>Create and manage your SatStacker account</li>
            <li>Authenticate you during login</li>
            <li>Secure your access to the app</li>
          </ul>

          <p className="text-gray-700">
            We do not use Google or Apple sign-in data for advertising or marketing.
          </p>

          <p className="text-gray-700">
            If you choose <strong>“Hide My Email”</strong> when using Sign in with
            Apple, Apple provides us with a private relay email address. We use it
            only to communicate with you about your account (e.g., verification or
            support).
          </p>
        </section>

        {/* How We Use Data */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            How We Use Your Information
          </h2>

          <p>We use your information to:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Provide and operate the SatStacker app and its features</li>
            <li>Maintain and secure your account and authentication</li>
            <li>Store and apply your settings and preferences</li>
            <li>Connect your SatStacker account to supported third-party providers when authorized by you</li>
            <li>Operate provider-connected planning, automation, and tracking features</li>
            <li>Operate SatStacker-powered functionality made available through approved third-party platforms</li>
            <li>Provide customer support and respond to requests</li>
            <li>Comply with applicable laws and enforce our terms</li>
          </ul>
        </section>

        {/* Data Storage & Security */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Data Storage and Security
          </h2>

          <p>
            We store user information on secure systems and use reasonable
            administrative, technical, and physical safeguards designed to protect
            it. Access is limited to authorized systems and personnel as needed to
            operate the service.
          </p>

          <p className="text-gray-700">
            No method of transmission or storage is 100% secure, but we work to
            protect your information using industry-standard practices.
          </p>
        </section>

        {/* Data Sharing */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Sharing of Information
          </h2>

          <p>
            We do <strong>not</strong> sell or rent your personal information.
          </p>

          <p>
            We may share information only in limited circumstances, such as:
          </p>

          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>Service providers:</strong> trusted vendors who help operate the
              app (e.g., hosting), under confidentiality obligations.
            </li>
            <li>
              <strong>Supported third-party providers:</strong> when you authorize a
              provider connection or use provider-connected features, as needed to
              provide the requested functionality.
            </li>
            <li>
              <strong>Approved third-party platforms:</strong> when SatStacker-powered
              functionality is made available through a platform, as needed to
              provide, support, secure, or improve that functionality.
            </li>
            <li>
              <strong>Legal and compliance:</strong> if required by law, regulation,
              legal process, or to protect rights and safety.
            </li>
          </ul>

          <p className="text-gray-700">
            <strong>Google user data:</strong> We do not share Google user data with
            third parties except as necessary to provide the service (e.g., secure
            authentication) or as required by law.
          </p>
        </section>

        {/* Third-Party Services and Connected Providers */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Third-Party Services and Connected Providers
          </h2>

          <p>
            SatStacker may integrate with supported third-party providers through
            OAuth, API access, platform integrations, or other account-linking
            methods. These integrations help you plan, automate, and track Bitcoin
            purchases or other provider-supported activity using your own account
            with the applicable provider.
          </p>

          <p>
            If you connect or use a supported third-party provider, SatStacker may
            receive information from that provider depending on the permissions you
            authorize or the integration made available. This may include account
            connection status, transaction-related information, plan execution
            information, balances, or other account data needed to operate
            SatStacker features.
          </p>

          <p className="text-gray-700">
            SatStacker does not receive your bank credentials from third-party
            providers. Any Bitcoin purchase, sale, exchange, transfer, custody,
            settlement, KYC/AML, eligibility, limits, and compliance controls occur
            directly between you and the applicable provider through your own
            provider account.
          </p>

          <p className="text-gray-700">
            You may disconnect provider access through SatStacker or through the
            provider where supported. Disconnecting a provider may limit or disable
            SatStacker features that require a connected account.
          </p>
        </section>

        {/* Third-Party Platform Use */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Third-Party Platform Use
          </h2>

          <p>
            SatStacker-powered tools, algorithms, or related functionality may be
            made available through approved third-party platforms. If you use
            SatStacker-powered functionality through a third-party platform, that
            platform may collect and process information under its own privacy
            policy and may share limited information with SatStacker as needed to
            provide, support, secure, or improve the SatStacker-powered
            functionality.
          </p>

          <p className="text-gray-700">
            Any transactions conducted through a third-party platform, and any
            associated custody, exchange, transfer, settlement, KYC/AML,
            eligibility, limits, and compliance controls, occur between you and the
            applicable platform or provider, not SatStacker.
          </p>
        </section>

        {/* Retention & Deletion */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Data Retention and Deletion
          </h2>

          <p>
            We retain personal information only for as long as necessary to operate
            your account and provide the service, unless a longer retention period
            is required by law.
          </p>

          <p>
            You may request deletion of your account and associated data at any time
            by contacting{" "}
            <a
              href="mailto:support@satstacker.app"
              className="text-blue-600 underline"
            >
              support@satstacker.app
            </a>
            . After we verify your request, we will delete your account data within
            a reasonable timeframe, subject to legal or security obligations.
          </p>
        </section>

        {/* International Availability and Data */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            International Availability and Data
          </h2>

          <p>
            SatStacker may be available in multiple countries or regions. If you use
            SatStacker outside the United States, your information may be processed
            in countries where we or our service providers operate, subject to
            applicable law.
          </p>

          <p className="text-gray-700">
            Third-party provider services may be provided by different entities,
            affiliates, platforms, or service providers depending on your country
            or region. Your use of any third-party provider or platform is governed
            by that provider’s or platform’s own terms and privacy policy.
          </p>
        </section>

        {/* AI / ML Training */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            AI / ML Model Training
          </h2>

          <p className="text-gray-700">
            SatStacker does not use Google user data (or Apple sign-in data) to train
            artificial intelligence or machine learning models.
          </p>
        </section>

        {/* Children */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Children’s Privacy
          </h2>

          <p className="text-gray-700">
            SatStacker is not directed to children under 13 (or the minimum age
            required in your jurisdiction). We do not knowingly collect personal
            information from children.
          </p>
        </section>

        {/* Changes */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Changes to This Policy
          </h2>

          <p className="text-gray-700">
            We may update this Privacy Policy from time to time. If we make material
            changes, we will update the effective date above and may provide
            additional notice within the app or on our website.
          </p>
        </section>

        {/* Contact */}
        <section className="space-y-3 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our data practices,
            contact{" "}
            <a
              href="mailto:support@satstacker.app"
              className="text-blue-600 underline"
            >
              support@satstacker.app
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
}